
import React, { useState, useMemo } from 'react';
import { DmnModel, Rule, ColumnDefinition, HitPolicy } from '../../types';
import { Button } from '../atoms/BaseComponents';
import { HeaderCell, HitPolicyCell, EditableCell } from '../molecules/TableParts';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { validateModel } from '../../utils/validation';

interface DecisionTableProps {
  model: DmnModel;
  onModelChange: (model: DmnModel) => void;
}

export const DecisionTable = ({ model, onModelChange }: DecisionTableProps) => {
  const [lastAddedRuleId, setLastAddedRuleId] = useState<string | null>(null);
  
  // --- Validation ---
  const validation = useMemo(() => validateModel(model), [model]);

  // --- Actions ---

  const addRule = () => {
    const newId = `r${Date.now()}`;
    const newRule: Rule = {
      id: newId,
      cells: {}
    };
    // Initialize empty cells
    model.columns.forEach(c => newRule.cells[c.id] = '');
    setLastAddedRuleId(newId);
    onModelChange({
      ...model,
      rules: [...model.rules, newRule]
    });
  };

  const removeRule = (ruleId: string) => {
    onModelChange({
      ...model,
      rules: model.rules.filter(r => r.id !== ruleId)
    });
  };

  const updateCell = (ruleId: string, colId: string, value: string) => {
    const updatedRules = model.rules.map(r => {
      if (r.id === ruleId) {
        return {
          ...r,
          cells: { ...r.cells, [colId]: value }
        };
      }
      return r;
    });
    onModelChange({ ...model, rules: updatedRules });
  };

  const addInputColumn = () => {
    const newCol: ColumnDefinition = {
      id: `c${Date.now()}`,
      label: 'New Input',
      type: 'input',
      dataType: 'string',
      variableName: 'input'
    };
    // Insert before outputs
    const firstOutputIndex = model.columns.findIndex(c => c.type === 'output');
    const newColumns = [...model.columns];
    if (firstOutputIndex === -1) {
        newColumns.push(newCol);
    } else {
        newColumns.splice(firstOutputIndex, 0, newCol);
    }
    onModelChange({ ...model, columns: newColumns });
  };

  const addOutputColumn = () => {
    const newCol: ColumnDefinition = {
        id: `c${Date.now()}`,
        label: 'New Output',
        type: 'output',
        dataType: 'string',
        variableName: 'output'
    };
    onModelChange({ ...model, columns: [...model.columns, newCol] });
  };

  const cycleHitPolicy = () => {
      const policies: HitPolicy[] = ['UNIQUE', 'FIRST', 'PRIORITY', 'ANY', 'COLLECT'];
      const currentIdx = policies.indexOf(model.hitPolicy);
      const nextIdx = (currentIdx + 1) % policies.length;
      onModelChange({ ...model, hitPolicy: policies[nextIdx] });
  };

  const onColumnUpdate = (id: string, def: Partial<ColumnDefinition>) => {
      const newColumns = model.columns.map(c => c.id === id ? { ...c, ...def } : c);
      onModelChange({ ...model, columns: newColumns });
  };

  // --- Derived State ---
  const inputColumns = model.columns.filter(c => c.type === 'input');
  const outputColumns = model.columns.filter(c => c.type === 'output');

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950 overflow-hidden">
      
      {/* Toolbar / Table Actions */}
      <div className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={addInputColumn}>
              <Plus size={14} /> Add Input
          </Button>
          <Button size="sm" variant="secondary" onClick={addOutputColumn}>
              <Plus size={14} /> Add Output
          </Button>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-2" />
          <Button size="sm" variant="secondary" onClick={() => {/* Toggle Annotations */}}>
              Annotations
          </Button>
        </div>

        {!validation.isValid && (
          <div className="flex items-center gap-2 px-3 py-1 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-semibold rounded-full border border-red-100 dark:border-red-800/50">
            <AlertCircle size={14} />
            {validation.errorCount} Validation Error{validation.errorCount > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Main Table Scroll Area */}
      <div className="flex-1 overflow-auto p-8">
        <div className="inline-block min-w-full shadow-lg rounded-lg overflow-hidden border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900">
            <table className="w-full border-collapse">
                <thead>
                    {/* Row 1: Group Headers */}
                    <tr>
                        <HitPolicyCell policy={model.hitPolicy} onClick={cycleHitPolicy} />
                        
                        {/* Input Header Group */}
                        {inputColumns.length > 0 && (
                            <th 
                                colSpan={inputColumns.length} 
                                className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wider py-1 border-r border-b border-slate-300 dark:border-slate-600"
                            >
                                Input Expression (When)
                            </th>
                        )}

                         {/* Output Header Group */}
                         {outputColumns.length > 0 && (
                            <th 
                                colSpan={outputColumns.length} 
                                className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[10px] font-bold uppercase tracking-wider py-1 border-b border-slate-300 dark:border-slate-600"
                            >
                                Output Values (Then)
                            </th>
                        )}
                        <th className="w-12 border-b border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900"></th>
                    </tr>

                    {/* Row 2: Columns */}
                    <tr>
                        <th className="bg-slate-50 dark:bg-slate-900 border-r border-b border-slate-300 dark:border-slate-600 text-xs font-mono text-slate-400 dark:text-slate-500">#</th>
                        {model.columns.map((col, idx) => (
                            <HeaderCell 
                                key={col.id} 
                                column={col} 
                                isLast={idx === model.columns.length - 1} 
                                onUpdate={onColumnUpdate}
                            />
                        ))}
                        <th className="bg-slate-50 dark:bg-slate-900 border-b border-slate-300 dark:border-slate-600 w-10"></th>
                    </tr>
                </thead>
                <tbody>
                    {model.rules.map((rule, idx) => (
                        <tr key={rule.id} className="group hover:bg-slate-50 dark:hover:bg-slate-900/50">
                            {/* Rule Number */}
                            <td className="bg-white dark:bg-slate-900 border-r border-b border-slate-200 dark:border-slate-700 text-center text-xs font-mono text-slate-400 dark:text-slate-500 w-12 group-hover:bg-slate-100 dark:group-hover:bg-slate-800">
                                {idx + 1}
                            </td>
                            
                            {/* Cells */}
                            {model.columns.map((col, colIdx) => (
                                <EditableCell 
                                    key={`${rule.id}-${col.id}`}
                                    value={rule.cells[col.id] || ''}
                                    onChange={(val) => updateCell(rule.id, col.id, val)}
                                    isOutput={col.type === 'output'}
                                    autoFocus={rule.id === lastAddedRuleId && colIdx === 0}
                                    isInvalid={validation.invalidCells[`${rule.id}-${col.id}`]}
                                />
                            ))}

                            {/* Actions */}
                            <td className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 group-hover:bg-slate-100 dark:group-hover:bg-slate-800 text-center w-10">
                                <button 
                                    onClick={() => removeRule(rule.id)}
                                    className="text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 p-1 rounded opacity-0 group-hover:opacity-100 transition-all"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </td>
                        </tr>
                    ))}
                    
                    {/* Add Rule Button Row */}
                    <tr>
                        <td className="bg-slate-50 dark:bg-slate-900/50 border-r border-slate-200 dark:border-slate-700 text-center font-mono text-xs text-slate-400 dark:text-slate-600 py-2">
                           +
                        </td>
                        <td colSpan={model.columns.length + 1} className="p-0 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                            <button 
                                onClick={addRule}
                                className="w-full h-8 text-xs font-bold text-slate-500 dark:text-slate-500 hover:text-primary dark:hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 uppercase tracking-wide"
                            >
                                <Plus size={14} /> Add Rule
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
      </div>

    </div>
  );
};
